import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import OrdersColumns from "../../../components/CustomTable/Columns/OrdersColumns";
import CustomTable from "../../../components/CustomTable/CustomTable";
import { useAuth } from "../../../context/AuthContext";
import { useFeedBack } from "../../../context/FeedBackContext";
import useAxios from "../../../hooks/useAxios";
import useOrders from "../../../hooks/useOrders";
import useOrdersTypes from "../../../hooks/useOrdersTypes";
import useServices from "../../../hooks/useServices";
import { mainPermissions } from "../../../util/MenuLinks";
import useZones from "../../../hooks/useZones";
import useOrderStatuses from "../../../hooks/useOrderStatuses";
import useCategories from "../../../hooks/useCategories";
import { FaRegFileExcel, FaTrash, FaFilter, FaChevronDown, FaChevronUp } from "react-icons/fa";
import UserHavePermission from "../../../util/UserHavePermission";

const Orders = () => {
    const { permissions, isSuperAdmin } = useAuth();
    const { setCustomAlert, setLoading } = useFeedBack();

    // Hook para manejar los parámetros de búsqueda
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // Estado inicial de los filtros basado en los parámetros de la URL
    const getInitialFilters = () => {
        return {
            page: parseInt(searchParams.get('page')) || 1,
            serviceIds: searchParams.get('serviceIds') || '',
            zoneId: searchParams.get('zoneId') || '',
            start: searchParams.get('start') || '',
            end: searchParams.get('end') || '',
            orderTypeId: searchParams.get('orderTypeId') || '',
            categoryId: searchParams.get('categoryId') || '',
            id: searchParams.get('id') || '',
            orderStatusCode: searchParams.get('orderStatusCode') || ''
        };
    };

    const initialFilters = getInitialFilters();
    const [filters, setFilters] = useState(initialFilters);
    const hasInitialActiveFilters = Object.entries(initialFilters).some(([key, val]) => key !== 'page' && val !== '' && val !== null && val !== undefined);
    const [showFilters, setShowFilters] = useState(hasInitialActiveFilters);

    const activeFiltersCount = Object.entries(filters).filter(([key, val]) => key !== 'page' && val !== '' && val !== null && val !== undefined).length;

    const [servicesFilters, setServicesFilters] = useState({
        currentUserServices: 'true',
        perPage: 500
    });

    const [selectedValues, setSelectedValues] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    const [{ services, loading: servicesLoading }, getServices] = useServices({ params: { ...servicesFilters } }, { useCache: false });
    const [{ zones, loading: zonesLoading }, getZones] = useZones({ params: { ...servicesFilters } }, { useCache: false });
    const [{ categories }] = useCategories({ params: { orderBy: 'path,asc' } }, { useCache: false });
    const [{ ordersTypes }, getOrdersTypes] = useOrdersTypes({ options: { useCache: false } });
    const [{ orderStatuses }, getOrderStatuses] = useOrderStatuses({ options: { useCache: false } });
    const [{ orders, total, numberOfPages, error: ordersError, loading }, getOrders] = useOrders({ params: { ...filters }, options: { useCache: false } });
    const [{ error: deleteError, loading: deleteLoading }, deleteProvider] = useAxios({ method: 'DELETE' }, { manual: true, useCache: false });
    const [{ loading: loadingDetailedExcels }, generateDetailedExcels] = useAxios({ url: '/orders/multiple-details', responseType: 'blob' }, { manual: true, useCache: false });

    // Actualizar la URL cuando cambien los filtros
    useEffect(() => {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (value && value !== '') {
                params.set(key, value.toString());
            }
        });

        // Reemplazar la URL actual con los nuevos parámetros
        setSearchParams(params);
    }, [filters, setSearchParams]);

    // Obtener órdenes cuando cambien los filtros
    useEffect(() => {
        getOrders();
    }, [filters]);

    useEffect(() => {
        setLoading?.({
            show: deleteLoading,
            message: 'Eliminando Pedidos'
        });
    }, [deleteLoading]);

    useEffect(() => {
        if (deleteError) {
            setCustomAlert({
                title: 'error',
                severity: 'danger',
                message: 'Ha ocurrido un error al eliminar.',
                show: true
            });
        }

        if (ordersError) {
            setCustomAlert({
                title: 'error',
                severity: 'danger',
                message: 'Ha ocurrido un error al obtener los pedidos.',
                show: true
            });
        }
    }, [deleteError, ordersError]);

    useEffect(() => {
        if (selectAll) {
            setSelectedValues(orders?.map?.((value) => value?.id) || []);
        } else {
            setSelectedValues([]);
        }
    }, [selectAll]);

    const handleDelete = (value) => {
        deleteProvider({ url: `/orders/${value?.id}` }).then((data) => {
            setCustomAlert({
                title: '¡Operación Exitosa!',
                severity: 'success',
                message: 'El pedido ha sido eliminado exitosamente.',
                show: true
            });
            getOrders();
        });
    };

    const handleSelectALL = () => {
        setSelectAll((oldSelectAll) => !oldSelectAll);
    };

    const handleSelectValue = (selectedValue) => {
        const value = selectedValues?.includes(Number(selectedValue?.id));
        if (value) {
            const newValues = selectedValues?.filter(n => n !== Number(selectedValue?.id));
            setSelectedValues(newValues);
        } else {
            setSelectedValues((oldSelectedValues) => [...oldSelectedValues, Number(selectedValue?.id)]);
        }
    };

    const handlePageChange = (page) => {
        setFilters((oldFilters) => {
            return {
                ...oldFilters,
                page: page
            };
        });
    };

    const handleDeleteSelected = () => {
        deleteProvider({ url: `/orders/multiple`, data: { ids: selectedValues } }).then((data) => {
            setCustomAlert({
                title: '¡Operación Exitosa!',
                severity: 'success',
                message: 'Los pedidos han sido eliminados exitosamente.',
                show: true
            });
            setSelectedValues([]);
            getOrders();
        });
    };

    const handleChange = (e) => {
        setFilters((oldFilters) => {
            return {
                ...oldFilters,
                [e.target.name]: e.target.value,
                page: 1 // Resetear a página 1 cuando cambian otros filtros
            };
        });
    };

    // Función para limpiar todos los filtros
    const handleClearFilters = () => {
        setFilters({
            page: 1,
            serviceIds: '',
            zoneId: '',
            start: '',
            end: '',
            orderTypeId: '',
            categoryId: '',
            id: '',
            orderStatusCode: ''
        });
    };

    const handleExcelsDetails = () => {
        generateDetailedExcels({ params: { orderIds: selectedValues } }).then((response) => {
            const excelBlobUrl = URL.createObjectURL(response?.data);
            const aToDownload = document.getElementById('downloadLink');
            aToDownload.href = excelBlobUrl;
            aToDownload?.click();
            window.URL.revokeObjectURL(excelBlobUrl);
        });
    }

    return (
        <div>
            {
                permissions?.includes?.(mainPermissions?.orders[1]) || isSuperAdmin ?
                    <div className="my-4 justify-content-end d-flex">
                        <Link to={"/dashboard"} className="btn btn-primary">
                            Crear Pedido
                        </Link>
                    </div>
                    :
                    null
            }

            {/* Selector de Modo de Vista & Filtros */}
            <div className="card p-3 mb-4 shadow-sm border">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold me-2">Vista:</span>
                        <div className="btn-group" role="group">
                            <Link to="/pedidos" className="btn btn-sm btn-primary active">
                                Tabla
                            </Link>
                            <Link to="/pedidos-grilla" className="btn btn-sm btn-outline-primary">
                                Grilla
                            </Link>
                            <Link to="/pedidos-lista" className="btn btn-sm btn-outline-primary">
                                Lista
                            </Link>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <button
                            className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <FaFilter />
                            <span>Filtros</span>
                            {activeFiltersCount > 0 && (
                                <span className="badge bg-warning text-dark ms-1">{activeFiltersCount}</span>
                            )}
                            {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                        {activeFiltersCount > 0 && (
                            <button className="btn btn-sm btn-outline-danger" onClick={handleClearFilters}>
                                Limpiar Filtros
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Filtros de Búsqueda Desplegables */}
            {showFilters && (
                <div className="card mb-4 p-3 shadow-sm border">
                    <div className="row">
                        <div className="col-12 mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 text-primary">Filtros de Búsqueda</h5>
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={handleClearFilters}
                                >
                                    Limpiar Filtros
                                </button>
                            </div>
                        </div>

                        <div className="col-md-3 mb-2">
                            <div className="form-group">
                                <label className="text-primary small fw-bold">Número de pedido</label>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={filters?.id}
                                    onChange={handleChange}
                                    name="id"
                                    placeholder="Número de pedido..."
                                />
                            </div>
                        </div>
                        <div className="col-md-3 mb-2">
                            <div className="form-group">
                                <label className="text-primary small fw-bold">Servicio</label>
                                <select name="serviceIds" className="form-control form-control-sm" value={filters?.serviceIds} onChange={handleChange}>
                                    <option value="">Seleccione uno</option>
                                    {services?.map((service, i) => (
                                        <option key={i} value={service?.id}>{service?.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3 mb-2">
                            <div className="form-group">
                                <label className="text-primary small fw-bold">Zonas</label>
                                <select name="zoneId" className="form-control form-control-sm" value={filters?.zoneId} onChange={handleChange}>
                                    <option value="">Seleccione uno</option>
                                    {zones?.map((zone, i) => (
                                        <option key={i} value={zone?.id}>{zone?.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3 mb-2">
                            <div className="form-group">
                                <label className="text-primary small fw-bold">Categoría</label>
                                <select name="categoryId" className="form-control form-control-sm" value={filters?.categoryId} onChange={handleChange}>
                                    <option value="">Todas</option>
                                    {categories?.map((cat, i) => (
                                        <option key={i} value={cat?.id}>{cat?.path || cat?.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label className="text-primary small fw-bold">Tipo de pedido</label>
                                <select name="orderTypeId" className="form-control form-control-sm" value={filters?.orderTypeId} onChange={handleChange}>
                                    <option value="">Seleccione uno</option>
                                    {ordersTypes?.map((orderType, i) => (
                                        <option key={i} value={orderType?.id}>{orderType?.displayText}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label className="text-primary small fw-bold">Estatus</label>
                                <select name="orderStatusCode" className="form-control form-control-sm" value={filters?.orderStatusCode} onChange={handleChange}>
                                    <option value="">Seleccione uno</option>
                                    {orderStatuses?.map((orderStatus, i) => (
                                        <option key={i} value={orderStatus?.code}>{orderStatus?.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-4 mb-2">
                            <div className="form-group">
                                <label className="text-primary small fw-bold">Fecha</label>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <input
                                            type="date"
                                            onChange={handleChange}
                                            className="form-control form-control-sm"
                                            name="start"
                                            value={filters?.start}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <input
                                            type="date"
                                            onChange={handleChange}
                                            className="form-control form-control-sm"
                                            name="end"
                                            value={filters?.end}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <CustomTable
                onDeleteSelected={handleDeleteSelected}
                onSelectValue={handleSelectValue}
                onSelectAll={handleSelectALL}
                loading={loading}
                selectAll={selectAll}
                title={'Pedidos'}
                updatePath={"/pedidos/detalles"}
                entity={"orders"}
                updateOptionString={'Ver Detalles'}
                onDelete={handleDelete}
                selectedValues={selectedValues}
                pages={numberOfPages}
                total={total}
                values={orders}
                currentPage={filters?.page}
                collumns={OrdersColumns}
                changePage={handlePageChange}
                filters={filters}
                excelUrl={'/orders/export/excel'}
                perPage={filters?.perPage}
                onPerPageChange={(e) => setFilters((oldFilters) => {
                    return {
                        ...oldFilters,
                        [e.target.name]: e.target.value,
                        page: 1
                    }
                })}
            />
            {
                selectedValues?.length > 0 &&
                <div
                    className="position-fixed shadow rounded"
                    style={{
                        bottom: 20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'white',
                    }}
                >
                    <div className="btn-group">
                        <div className="btn btn-primary text-white" style={{ width: 120, fontSize: 36, alignContent: 'center', textAlign: 'center', height: 100 }}>
                            {selectedValues?.length}
                        </div>
                        <button onClick={handleExcelsDetails} disabled={loadingDetailedExcels} className="btn btn-outline-success" style={{ width: 120, height: 100 }}>
                            {
                                loadingDetailedExcels ?
                                    <div class="spinner-border text-success" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                    :
                                    <>
                                        <FaRegFileExcel />
                                        <br />
                                        Descargar excels detalles
                                    </>
                            }
                        </button>
                    </div>
                </div>
            }
        </div>
    );
};

export default Orders;